import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { toast } from "../../utils/toast";
import {
  fetchCountries,
  fetchRegionByCountry,
  handleNormalSignupMethod,
  handleSignUp,
  loginWithCredentials,
  loginWithNormalCredentials,
  uploadMediaFile,
  uploadMediaFromUrl,
  verifyLinkedInUser,
  verifyOAuthUser,
} from "../../services/api";
import {
  AchievementDetails,
  fighterSponsorsRequestDtos,
  FightHistory,
  FightHistoryDto,
  FormData,
  LoginDto,
  RegisterDto,
  SocialMediaLink,
  State,
  UserData,
  YoutubeLinkDetails,
} from "../../types/types";

const socialMediaPlatforms = [
  "YouTube",
  "Facebook",
  "Instagram",
  "Twitter",
  "LinkedIn",
];

const SignUpSetupLogics = () => {
  const { t, i18n } = useTranslation("signUpSetupLogic");
  const location = useLocation();
  const storedUserInfo = JSON.parse(
    localStorage.getItem("linkedin_user_info") || "{}",
  );

  const { email, phone, password, linkedIn, ...linkedInUserInfo } =
    location.state || storedUserInfo || {};

  const isLinkedInFlow =
    linkedIn || storedUserInfo.email || linkedInUserInfo.email;

  const [state, setState] = useState<State>({
    loading: true,
    userData: null,
    error: false,
    stage: "loading",
    formData: {
      profileType: undefined,
      email: undefined,
      gender: undefined,
      phone: "",
      password: undefined,
      country: undefined,
      region: undefined,
      nationality: undefined,
      profileImageUrl: undefined,
      profileImageFile: undefined,
      videoFile: undefined,
      bio: undefined,
      provider: undefined,
      fighterDto: {
        heightUnit: "cm",
        weightUnit: "kgs",
        reachUnit: "cm",
        ytLinkDetailsDto: [],
        socialMediaLinks: [],
        achievementDetails: [{ text: "" }],
        fightHistoryDetailsDto: [],
        fighterSponsorsRequestDtos: [],
      },
      managerDto: {},
    },
    canGoBack: false,
  });
  const [isEmailExistsError, setIsEmailExistsError] = useState<boolean>(false);
  const [countries, setCountries] = useState<string[]>([]);
  const [regions, setRegions] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState<boolean>(false);
  const [activeMember, setActiveMember] = useState<boolean>(false);
  const [selectedTrainingDisciplines, setSelectedTrainingDisciplines] =
    useState<string[]>([]);
  const [selectedWeightClasses, setSelectedWeightClasses] = useState<string[]>(
    [],
  );
  const [apiError, setApiError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string | undefined>
  >({});

  useEffect(() => {
    const savedLanguage = localStorage.getItem("i18nextLng") || "en";
    console.log("storedUserInfo ", storedUserInfo);
    i18n.changeLanguage(savedLanguage);
  }, [i18n]);

  const validateField = (
    name: string,
    value: any,
    dtoType?: "fighterDto" | "managerDto",
    selectedUnit?: string,
    selectedWeightUnit?: string,
    selectedReachUnit?: string,
  ): string | undefined => {
    switch (name) {
      case "fighterDto.email":
      case "managerDto.email":
        if (!value) return t("validation.email.required");
        if (!/\S+@\S+\.\S+/.test(value)) return t("validation.email.invalid");
        break;

      case "fighterDto.firstName":
      case "managerDto.firstName":
        if (!value) return t("validation.firstName.required");
        break;

      case "profileImageUrl": {
        if (!value) {
          return t("validation.profileImageFile.required");
        }
        break;
      }

      case "fighterDto.lastName":
      case "managerDto.lastName":
        if (!value) return t("validation.lastName.required");
        break;

      case "managerDto.type":
        if (!value) return t("validation.managerType.required");
        break;

      case "managerDto.organizationName":
        if (!value) return t("validation.organizationName.required");
        break;

      case "gender":
        if (!value) return "Please select gender";
        break;

      case "fighterDto.height": {
        if (!value) return t("validation.height.required");
        const heightNum = Number(value);
        if (selectedUnit === "cm" && (heightNum < 100 || heightNum > 250))
          return t("valid_height_required");
        if (selectedUnit === "ft" && (heightNum < 3 || heightNum > 8))
          return t("valid_height_required");
        if (selectedUnit === "inch" && (heightNum < 36 || heightNum > 96))
          return t("valid_height_required");
        break;
      }

      case "fighterDto.weight": {
        if (!value) return t("validation.weight.required");
        const weightNum = Number(value);
        if (selectedWeightUnit === "kgs" && (weightNum < 30 || weightNum > 200))
          return t("valid_weight_required");
        if (selectedWeightUnit === "lbs" && (weightNum < 66 || weightNum > 440))
          return t("valid_weight_required");
        break;
      }

      case "fighterDto.reach": {
        if (!value) return t("validation.reach.required");
        const reachNum = Number(value);
        if (selectedReachUnit === "cm" && (reachNum < 100 || reachNum > 250))
          return t("valid_reach_required");
        if (selectedReachUnit === "ft" && (reachNum < 3 || reachNum > 8))
          return t("valid_reach_required");
        if (selectedReachUnit === "inch" && (reachNum < 36 || reachNum > 100))
          return t("valid_reach_required");
        break;
      }

      case "fighterDto.stance":
        if (!value) return t("validation.stance.required");
        break;

      case "fighterDto.fightStyleDescription":
        if (!value || value.trim() === "")
          return t("validation.fightStyleDescription.required");
        break;

      case "fighterDto.dateOfBirth": {
        if (!value) return t("validation.dateOfBirth.required");
        const dob = new Date(value);
        const today = new Date();
        const age = today.getFullYear() - dob.getFullYear();
        if (age < 18) return t("validation.dateOfBirth.tooYoung");
        break;
      }

      case "country":
      case "fighterDto.country":
      case "managerDto.country":
        if (!value) return t("validation.country.required");
        break;

      case "nationality":
        if (!value) return t("validation.nationality.required");
        break;

      case "region":
      case "fighterDto.region":
      case "managerDto.region":
        if (!value) return t("validation.region.required");
        break;

      case "achievementDetails.text":
        if (!value) return t("validation.achievementDetails.text.required");
        break;
    }
    return undefined;
  };

  const validateForm = (formData: FormData, stage: State["stage"]): boolean => {
    const errors: Record<string, string | undefined> = {};
    const fieldsToValidate = (() => {
      switch (stage) {
        case "personalDetails":
          return [
            {
              name: "firstName",
              dtoType:
                state.formData.profileType === "FIGHTER"
                  ? "fighterDto"
                  : "managerDto",
            },
            {
              name: "lastName",
              dtoType:
                state.formData.profileType === "FIGHTER"
                  ? "fighterDto"
                  : "managerDto",
            },
            { name: "gender" },
            ...(state.formData.profileType === "FIGHTER"
              ? [{ name: "dateOfBirth", dtoType: "fighterDto" }]
              : []),
            ...(state.formData.profileType === "ORGANIZATION"
              ? [{ name: "organizationName", dtoType: "managerDto" }]
              : []),
          ];
        case "locationDetails":
          return [{ name: "country" }, { name: "nationality" }];
        case "contactDetails":
          return [{ name: "email" }, { name: "phone" }, { name: "bio" }];
        case "profileImageUpload":
          return [{ name: "profileImageFile" }];
        case "fighterFormBasic":
          return [
            { name: "height", dtoType: "fighterDto" },
            { name: "weight", dtoType: "fighterDto" },
            { name: "reach", dtoType: "fighterDto" },
            { name: "stance", dtoType: "fighterDto" },
          ];
        case "fighterFormDetails":
          return [{ name: "fightStyleDescription", dtoType: "fighterDto" }];
        case "managerForm":
          return [
            { name: "firstName", dtoType: "managerDto" },
            { name: "lastName", dtoType: "managerDto" },
            ...(state.formData.profileType === "MANAGER"
              ? [{ name: "type", dtoType: "managerDto" }]
              : []),
          ];
        case "videoUpload":
          return [{ name: "videoFile" }];
        case "fightHistory":
          return [{ name: "fightHistoryDetailsDto" }];
        default:
          return [];
      }
    })();

    fieldsToValidate.forEach(({ name, dtoType }) => {
      const typedDtoType = dtoType as "fighterDto" | "managerDto" | undefined;
      const dto = typedDtoType
        ? (formData[typedDtoType] as Record<string, any>)
        : formData;
      const value = typedDtoType
        ? dto?.[name]
        : formData[name as keyof FormData];

      const error = validateField(
        typedDtoType ? `${typedDtoType}.${name}` : name,
        value,
        typedDtoType,
        formData.fighterDto?.heightUnit,
        formData.fighterDto?.weightUnit,
        formData.fighterDto?.reachUnit,
      );

      if (error) {
        errors[typedDtoType ? `${typedDtoType}.${name}` : name] = error;
      }
    });

    if (stage === "contactDetails") {
      const socialLinks =
        state.formData.profileType === "FIGHTER"
          ? formData.fighterDto?.socialMediaLinks || []
          : formData.managerDto?.socialMediaLinks || [];

      socialLinks.forEach((link, index) => {
        const hasType = Boolean(link.type?.trim());
        const hasUrl = Boolean(link.url?.trim());

        if (!hasType && !hasUrl) {
          return;
        }

        if (!hasType) {
          errors[`socialMediaLinks.${index}.type`] =
            "Social Media Platform is required";
        }
        if (!hasUrl) {
          errors[`socialMediaLinks.${index}.url`] = "URL is required";
        } else {
          try {
            const url = new URL(link.url);
            if (!["http:", "https:"].includes(url.protocol)) {
              errors[`socialMediaLinks.${index}.url`] = "Enter a valid URL";
            }
          } catch {
            errors[`socialMediaLinks.${index}.url`] = "Enter a valid URL";
          }
        }
      });
    }

    if (stage === "fightHistory") {
      const fightHistories = formData.fighterDto?.fightHistoryDetailsDto || [];
      console.log("fighter History----------", fightHistories);
      if (fightHistories.length === 0) {
        errors["fighterDto.fightHistoryDetailsDto"] = t(
          "validation.fightHistory.required",
        );
        setValidationErrors((prev) => ({
          ...prev,
          "fighterDto.fightHistoryDetailsDto": t(
            "validation.fightHistory.required",
          ),
        }));
      }
    }

    if (stage === "profileImageUpload") {
      const hasFile =
        formData.profileImageFile && formData.profileImageFile !== null;
      const hasFileUrl =
        formData.profileImageUrl && formData.profileImageUrl.trim() !== "";

      if (!hasFile && !hasFileUrl) {
        errors["profileImageFile"] = t("validation.profileImageFile.required");
        setValidationErrors((prev) => ({
          ...prev,
          profileImageFile: t("validation.profileImageFile.required"),
        }));
      } else {
        console.log("no error");
      }
    }

    if (stage === "fighterFormBasic") {
      if (selectedTrainingDisciplines.length === 0)
        errors["fighterDto.trainingDisciplines"] = t(
          "validation.trainingDisciplines.required",
        );
      if (selectedWeightClasses.length === 0)
        errors["fighterDto.weightClasses"] = t(
          "validation.weightClasses.required",
        );
    }

    if (stage === "videoUpload" && formData.fighterDto?.ytLinkDetailsDto) {
      formData.fighterDto.ytLinkDetailsDto.forEach((link, index) => {
        if (
          link.ytLink &&
          !/^https?:\/\/[^\s/$.?#].[^\s]*$/.test(link.ytLink)
        ) {
          errors[`ytLinkDetailsDto.${index}.youtubeLink`] = t(
            "validation.youTubeLink.invalid",
          );
        }
        if (link.shortDescription && link.shortDescription.length > 200) {
          errors[`ytLinkDetailsDto.${index}.shortDescription`] = t(
            "validation.youTubeLink.maxLength",
          );
        }
      });
    }

    if (stage === "sponsorStage") {
      const sponsors = formData.fighterDto?.fighterSponsorsRequestDtos || [];

      sponsors.forEach((sponsor, index) => {
        if (!sponsor.name?.trim()) {
          errors[`fighterDto.fighterSponsorsRequestDtos.${index}.name`] = t(
            "sponsor.nameRequired",
          );
        }
      });
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const focusFirstInvalidField = (): void => {
    setTimeout(() => {
      const firstErrorElement = document.querySelector(
        '[aria-invalid="true"], [data-invalid="true"]',
      ) as HTMLElement | null;

      if (firstErrorElement) {
        firstErrorElement.focus();
        firstErrorElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }, 100);
  };

  useEffect(() => {
    const initializeData = async (): Promise<void> => {
      try {
        const linkedInData = storedUserInfo.email
          ? storedUserInfo
          : linkedInUserInfo;
        let response = null;
        const hasValidProps = email && password;

        if (isLinkedInFlow && linkedInData.email) {
          const isVerified = await verifyLinkedInUser(
            linkedInData.email,
            "linkedIn",
          );
          if (isVerified) {
            const loginData: LoginDto = {
              username: linkedInData.email,
              password: "linkedIn@123",
            };
            const loginResponse = await loginWithNormalCredentials(loginData);
            if (loginResponse) {
              localStorage.setItem(
                "loginResponse",
                JSON.stringify(loginResponse),
              );
              localStorage.removeItem("linkedin_user_info");
              localStorage.setItem(
                "i18nextLng",
                loginResponse.preferredLanguage,
              );
              window.location.reload();
              window.location.href = "/dashboard";
              return;
            } else {
              throw new Error(t("errors.loginFailed"));
            }
          } else {
            const prefilledFormData: FormData = {
              email: linkedInData.email,
              phone: linkedInData.phone,
              password: "linkedIn@123",
              country: linkedInData.country,
              region: linkedInData.region,
              nationality: linkedInData.nationality,
              profileImageUrl: linkedInData.profileImage,
              bio: linkedInData.bio || "",
              provider: "linkedIn",
              fighterDto: {
                firstName: linkedInData.firstname || linkedInData.firstName,
                lastName: linkedInData.lastName,
                heightUnit: "cm",
                weightUnit: "kgs",
                reachUnit: "cm",
                ytLinkDetailsDto: [],
                socialMediaLinks: linkedInData.socialMediaLinks || [],
              },
              managerDto: {
                firstName: linkedInData.firstName,
                lastName: linkedInData.lastName,
              },
            };

            const countriesData = await fetchCountries();
            const countryList = countriesData
              .map((country) => country.trim())
              .filter(Boolean);
            setCountries(countryList);

            setState({
              loading: false,
              userData: null,
              error: false,
              stage: "roleSelection",
              formData: prefilledFormData,
              canGoBack: false,
            });
            return;
          }
        } else if (hasValidProps) {
          const prefilledFormData: FormData = {
            email: email,
            password: password,
            country: linkedInData?.country,
            region: linkedInData?.region,
            nationality: linkedInData?.nationality,
            profileImageUrl: linkedInData?.profileImage,
            bio: linkedInData?.bio || "",
            fighterDto: {
              firstName: linkedInData?.firstName,
              lastName: linkedInData?.lastName,
              heightUnit: "cm",
              weightUnit: "kgs",
              reachUnit: "cm",
              ytLinkDetailsDto: [],
              socialMediaLinks: linkedInData?.socialMediaLinks || [],
            },
            managerDto: {
              firstName: linkedInData?.firstName,
              lastName: linkedInData?.lastName,
            },
          };

          const countriesData = await fetchCountries();
          const countryList = countriesData
            .map((country) => country.trim())
            .filter(Boolean);
          setCountries(countryList);

          setState({
            loading: false,
            userData: null,
            error: false,
            stage: "roleSelection",
            formData: prefilledFormData,
            canGoBack: false,
          });
        } else {
          response = await verifyOAuthUser();
          if (response) {
            let profileImageUrl = "";
            if (linkedInData?.profileImage) {
              profileImageUrl = await uploadMediaFromUrl(
                linkedInData.profileImage,
              );
            } else if (response.profileImage) {
              profileImageUrl = await uploadMediaFromUrl(response.profileImage);
            }
            const prefilledFormData: FormData = {
              email: email || linkedInData?.email || response.email,
              phone: response.phone,
              password: password,
              country: linkedInData?.country || response.country,
              region: linkedInData?.region || response.region,
              nationality: linkedInData?.nationality || response.nationality,
              profileImageUrl: profileImageUrl,
              bio: linkedInData?.bio || response.bio || "",
              fighterDto: {
                firstName: linkedInData?.firstName || response.firstName,
                lastName: linkedInData?.lastName || response.lastName,
                heightUnit: "cm",
                weightUnit: "kgs",
                reachUnit: "cm",
                ytLinkDetailsDto: [],
                socialMediaLinks: response.socialMediaLinks || [],
              },
              managerDto: {
                firstName: linkedInData?.firstName || response.firstName,
                lastName: linkedInData?.lastName || response.lastName,
              },
            };

            const countriesData = await fetchCountries();
            const countryList = countriesData
              .map((country) => country.trim())
              .filter(Boolean);
            setCountries(countryList);

            setState({
              loading: false,
              userData: response as unknown as UserData,
              error: false,
              stage: "roleSelection",
              formData: prefilledFormData,
              canGoBack: false,
            });
          } else {
            const loginData: LoginDto = {
              username: null,
              password: null,
            };
            const loginResponse = await loginWithCredentials(loginData);
            if (loginResponse) {
              localStorage.setItem(
                "loginResponse",
                JSON.stringify(loginResponse),
              );
              localStorage.setItem(
                "i18nextLng",
                loginResponse.preferredLanguage,
              );
              window.location.reload();
              window.location.href = "/dashboard";
            } else {
              setApiError(t("errors.authenticationFailed"));
              setState((prev) => ({
                ...prev,
                loading: false,
                error: true,
                stage: "error",
                previousStage: prev.stage,
              }));
            }
          }
        }
      } catch (error: any) {
        let errorMessage = t("errors.generic");
        let isEmailExists = false;
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
          if (errorMessage.toLowerCase().includes("email already exists")) {
            isEmailExists = true;
            errorMessage = t("errors.emailExists");
          }
        } else if (error.response?.data?.error) {
          errorMessage = error.response.data.error;
          if (errorMessage.toLowerCase().includes("email already exists")) {
            isEmailExists = true;
            errorMessage = t("errors.emailExists");
          }
        } else if (error.message) {
          errorMessage = error.message;
        }
        setApiError(errorMessage);
        setIsEmailExistsError(isEmailExists);
        setState((prev) => ({
          ...prev,
          loading: false,
          error: true,
          stage: "error",
          previousStage: prev.stage,
        }));
      }
    };

    initializeData();
  }, [location.state, phone, password, t]);

  useEffect(() => {
    const fetchRegions = async (): Promise<void> => {
      if (state.stage === "locationDetails" && state.formData.country) {
        try {
          const regionData = await fetchRegionByCountry(state.formData.country);
          setRegions([regionData.region].filter(Boolean));
        } catch (error) {
          setRegions([]);
        }
      } else {
        setRegions([]);
      }
    };
    fetchRegions();
  }, [state.stage, state.formData.country]);

  useEffect(() => {
    setSelectedTrainingDisciplines(
      state.formData.fighterDto?.trainingDisciplines || [],
    );
    setSelectedWeightClasses(state.formData.fighterDto?.weightClasses || []);
  }, [
    state.formData.fighterDto?.trainingDisciplines,
    state.formData.fighterDto?.weightClasses,
  ]);

  const handleRoleSelection = (
    role: "FIGHTER" | "MANAGER" | "ORGANIZATION",
  ): void => {
    setState((prev) => ({
      ...prev,
      stage: "personalDetails",
      formData: {
        ...prev.formData,
        profileType: role,
        managerDto: {
          ...prev.formData.managerDto,
          type: undefined,
          organizationName: undefined,
        },
      },
      canGoBack: true,
    }));
    setValidationErrors({});
  };

  const handleNext = (
    e: FormEvent<HTMLFormElement>,
    nextStage: State["stage"],
  ): void => {
    e.preventDefault();

    if (
      state.stage === "fighterFormDetails" &&
      nextStage === "fightHistory" &&
      !state.formData.fighterDto?.fightStyleDescription?.trim()
    ) {
      setValidationErrors((prev) => ({
        ...prev,
        "fighterDto.fightStyleDescription": t(
          "validation.fightStyleDescription.required",
        ),
      }));
      setTimeout(() => {
        const firstErrorElement = document.querySelector(
          '[aria-invalid="true"]',
        ) as HTMLElement | null;

        if (firstErrorElement) {
          firstErrorElement.focus();
          firstErrorElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 100);
      return;
    }

    const isValid = validateForm(state.formData, state.stage);

    if (!isValid) {
      focusFirstInvalidField();
      return;
    }

    setState((prev) => ({
      ...prev,
      stage: nextStage,
      canGoBack: true,
    }));
    setValidationErrors({});
  };

  const handleFormChange = (
    value:
      | string
      | boolean
      | AchievementDetails[]
      | SocialMediaLink[]
      | YoutubeLinkDetails[]
      | FightHistory[]
      | fighterSponsorsRequestDtos[],
    name: string,
    dtoType?: "fighterDto" | "managerDto",
  ): void => {
    setState((prev) => {
      let updatedFormData: FormData;

      if (dtoType) {
        updatedFormData = {
          ...prev.formData,
          [dtoType]: {
            ...prev.formData[dtoType],
            [name]: value,
          },
        };
      } else {
        updatedFormData = {
          ...prev.formData,
          [name]: value,
        };
      }

      return {
        ...prev,
        formData: updatedFormData,
      };
    });

    const fieldKey = dtoType ? `${dtoType}.${name}` : name;

    setValidationErrors((prev) => ({
      ...prev,
      [fieldKey]: undefined,
    }));

    if (name === "region" && !value) {
      setValidationErrors((prev) => {
        const updated = { ...prev };
        delete updated["region"];
        return updated;
      });
      return;
    }
  };

  const toggleMultiSelectOption = (
    field: "trainingDisciplines" | "weightClasses",
    values: string[],
  ): void => {
    const setSelection =
      field === "trainingDisciplines"
        ? setSelectedTrainingDisciplines
        : setSelectedWeightClasses;
    setSelection(values);

    setState((prev) => ({
      ...prev,
      formData: {
        ...prev.formData,
        fighterDto: {
          ...prev.formData.fighterDto,
          [field]: values,
        },
      },
    }));

    setValidationErrors((prev) => ({
      ...prev,
      [`fighterDto.${field}`]:
        values.length === 0 ? t(`validation.${field}.required`) : undefined,
    }));
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const { name, files } = e.target;

    if (files && files.length > 0) {
      const file = files[0];

      const maxSizeInBytes =
        name === "videoFile" ? 50 * 1024 * 1024 : 2 * 1024 * 1024;

      if (file.size > maxSizeInBytes) {
        setValidationErrors((prev) => ({
          ...prev,
          [name]:
            name === "videoFile"
              ? "Video size must be less than 50MB"
              : "Image size must be less than 2MB",
        }));
        return;
      }

      const error = validateField(name, file);

      setValidationErrors((prev) => ({
        ...prev,
        [name]: error,
      }));

      if (!error) {
        if (name === "profileImageFile") {
          console.log("No error is present");
          setState((prev) => ({
            ...prev,
            formData: {
              ...prev.formData,
              profileImageFile: file,
              profileImageUrl: undefined,
            },
          }));
        } else if (name === "videoFile") {
          setState((prev) => ({
            ...prev,
            formData: {
              ...prev.formData,
              videoFile: file,
            },
          }));
        }
      }

      console.log("File change handling completed");
    }
  };

  const removeVideo = (): void => {
    setState((prev) => ({
      ...prev,
      formData: {
        ...prev.formData,
        videoFile: undefined,
      },
    }));
    setValidationErrors((prev) => ({
      ...prev,
      videoFile: undefined,
    }));
  };

  const addYouTubeLink = (): void => {
    setState((prev) => ({
      ...prev,
      formData: {
        ...prev.formData,
        fighterDto: {
          ...prev.formData.fighterDto,
          ytLinkDetailsDto: [
            ...(prev.formData.fighterDto?.ytLinkDetailsDto || []),
            { ytLink: "", shortTitle: "", shortDescription: "" },
          ],
        },
      },
    }));
  };

  const updateYouTubeLink = (
    index: number,
    field: string,
    value: string,
  ): void => {
    setState((prev) => {
      const updatedLinks = [
        ...(prev.formData.fighterDto?.ytLinkDetailsDto || []),
      ];
      updatedLinks[index] = { ...updatedLinks[index], [field]: value };
      return {
        ...prev,
        formData: {
          ...prev.formData,
          fighterDto: {
            ...prev.formData.fighterDto,
            ytLinkDetailsDto: updatedLinks,
          },
        },
      };
    });

    const error =
      field === "youtubeLink" && value
        ? validateField("youtubeLink", value)
        : undefined;
    setValidationErrors((prev) => ({
      ...prev,
      [`ytLinkDetailsDto.${index}.${field}`]:
        error ||
        (field === "shortDescription" && value.length > 200
          ? t("validation.youTubeLink.maxLength")
          : undefined),
    }));
  };

  const removeYouTubeLink = (index: number): void => {
    setState((prev) => {
      const updatedLinks = (
        prev.formData.fighterDto?.ytLinkDetailsDto || []
      ).filter((_, i) => i !== index);
      return {
        ...prev,
        formData: {
          ...prev.formData,
          fighterDto: {
            ...prev.formData.fighterDto,
            ytLinkDetailsDto: updatedLinks,
          },
        },
      };
    });
    setValidationErrors((prev) => {
      const newErrors = { ...prev };
      Object.keys(prev).forEach((key) => {
        if (key.startsWith(`ytLinkDetailsDto.${index}`)) {
          delete newErrors[key];
        }
      });
      return newErrors;
    });
  };

  const addSocialMediaLink = (): void => {
    setState((prev) => {
      const isFighter = prev.formData.profileType === "FIGHTER";

      const currentLinks = isFighter
        ? prev.formData.fighterDto?.socialMediaLinks || []
        : prev.formData.managerDto?.socialMediaLinks || [];

      const updatedLinks = [...currentLinks, { type: "", url: "" }];

      return {
        ...prev,
        formData: {
          ...prev.formData,
          ...(isFighter
            ? {
                fighterDto: {
                  ...prev.formData.fighterDto,
                  socialMediaLinks: updatedLinks,
                },
              }
            : {
                managerDto: {
                  ...prev.formData.managerDto,
                  socialMediaLinks: updatedLinks,
                },
              }),
        },
      };
    });
  };

  const updateSocialMediaLink = (
    index: number,
    field: "type" | "url",
    value: string,
  ): void => {
    setState((prev) => {
      const isFighter = prev.formData.profileType === "FIGHTER";

      const existingLinks = isFighter
        ? prev.formData.fighterDto?.socialMediaLinks || []
        : prev.formData.managerDto?.socialMediaLinks || [];

      const updatedLinks = [...existingLinks];
      updatedLinks[index] = { ...updatedLinks[index], [field]: value };

      return {
        ...prev,
        formData: {
          ...prev.formData,
          ...(isFighter
            ? {
                fighterDto: {
                  ...prev.formData.fighterDto,
                  socialMediaLinks: updatedLinks,
                },
              }
            : {
                managerDto: {
                  ...prev.formData.managerDto,
                  socialMediaLinks: updatedLinks,
                },
              }),
        },
      };
    });
    setValidationErrors((prev) => ({
      ...prev,
      [`socialMediaLinks.${index}.${field}`]: undefined,
    }));
  };

  const handleSuccessPopupClose = (): void => {
    setShowSuccessPopup(false);
    window.location.href = "/login";
  };

  const removeSocialMediaLink = (index: number): void => {
    setState((prev) => {
      const isFighter = prev.formData.profileType === "FIGHTER";

      const currentLinks = isFighter
        ? prev.formData.fighterDto?.socialMediaLinks || []
        : prev.formData.managerDto?.socialMediaLinks || [];

      const updatedLinks = currentLinks.filter((_, i) => i !== index);

      return {
        ...prev,
        formData: {
          ...prev.formData,
          ...(isFighter
            ? {
                fighterDto: {
                  ...prev.formData.fighterDto,
                  socialMediaLinks: updatedLinks,
                },
              }
            : {
                managerDto: {
                  ...prev.formData.managerDto,
                  socialMediaLinks: updatedLinks,
                },
              }),
        },
      };
    });

    setValidationErrors((prev) => {
      const newErrors = { ...prev };
      Object.keys(prev).forEach((key) => {
        if (key.startsWith(`socialMediaLinks.${index}`)) {
          delete newErrors[key];
        }
      });
      return newErrors;
    });
  };

  const handleFightHistoriesChange = (updatedHistories: FightHistoryDto[]) => {
    console.log("Updated fight histories:", updatedHistories);
    setState((prev) => ({
      ...prev,
      formData: {
        ...prev.formData,
        fighterDto: {
          ...prev.formData.fighterDto!,
          fightHistoryDetailsDto: updatedHistories,
        },
      },
    }));
  };

  const handleFormSubmit = async (
    e: FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault();

    if (!validateForm(state.formData, state.stage)) {
      focusFirstInvalidField();
      return;
    }

    if (
      state.formData.profileType === "FIGHTER" &&
      (!state.formData.fighterDto?.fightHistoryDetailsDto ||
        state.formData.fighterDto.fightHistoryDetailsDto.length === 0)
    ) {
      toast.error("Please add at least one fight history before submitting.");
      return;
    }

    setIsSubmitting(true);

    try {
      const registerDto: RegisterDto = {
        email: state.formData.email!,
        phone: state.formData.phone ? state.formData.phone : null,
        gender: state.formData.gender!,
        password: state.formData.password,
        country: state.formData.country,
        region: state.formData.region,
        nationality: state.formData.nationality,
        profileType: state.formData.profileType!,
        profileImageUrl: state.formData.profileImageFile
          ? undefined
          : state.formData.profileImageUrl,
        bio: state.formData.bio,
        provider: state.formData.provider,
      };

      if (
        state.formData.profileType === "FIGHTER" &&
        state.formData.fighterDto
      ) {
        registerDto.fighterDto = {
          firstName: state.formData.fighterDto.firstName!,
          lastName: state.formData.fighterDto.lastName!,
          nickname: state.formData.fighterDto.nickname,
          country: state.formData.fighterDto.country || state.formData.country!,
          stance: state.formData.fighterDto.stance!,
          team: state.formData.fighterDto.team,
          gym: state.formData.fighterDto.gym,
          socialMediaLinks: state.formData.fighterDto.socialMediaLinks?.filter(
            (link) => link.type?.trim() || link.url?.trim(),
          ),
          fightStyleDescription:
            state.formData.fighterDto.fightStyleDescription!,
          achievementDetails: state.formData.fighterDto.achievementDetails,
          isTitleHolder: state.formData.fighterDto.isTitleHolder,
          height: Number(state.formData.fighterDto.height)!,
          heightUnit: state.formData.fighterDto.heightUnit!,
          weight: Number(state.formData.fighterDto.weight)!,
          weightUnit: state.formData.fighterDto.weightUnit!,
          reach: Number(state.formData.fighterDto.reach)!,
          reachUnit: state.formData.fighterDto.reachUnit!,
          dateOfBirth: state.formData.fighterDto.dateOfBirth!,
          trainingDisciplines:
            state.formData.fighterDto.trainingDisciplines || [],
          weightClasses: state.formData.fighterDto.weightClasses || [],
          ytLinkDetailsDto: state.formData.fighterDto.ytLinkDetailsDto || [],
          fightHistoryDetailsDto:
            state.formData.fighterDto.fightHistoryDetailsDto?.map(
              (history) => ({
                ...history,
                organizationName: history.organizationName || "Unknown",
              }),
            ),
          fighterSponsorsRequestDtos:
            state.formData.fighterDto.fighterSponsorsRequestDtos || [],
        };
      } else if (
        (state.formData.profileType === "MANAGER" ||
          state.formData.profileType === "ORGANIZATION") &&
        state.formData.managerDto
      ) {
        registerDto.managerDto = {
          firstName: state.formData.managerDto.firstName!,
          lastName: state.formData.managerDto.lastName!,
          ...(state.formData.profileType === "MANAGER"
            ? { type: state.formData.managerDto.type! }
            : {}),
          ...(state.formData.profileType === "ORGANIZATION"
            ? {
                organizationName: state.formData.managerDto.organizationName!,
              }
            : {}),
          socialMediaLinks: state.formData.managerDto.socialMediaLinks?.filter(
            (link) => link.type?.trim() || link.url?.trim(),
          ),
        };
      }

      const isNormalUser = state.formData.email && state.formData.password;

      if (isLinkedInFlow && state.formData.email) {
        const response = await handleNormalSignupMethod(
          registerDto,
          state.formData.profileImageFile,
          state.formData.videoFile,
        );
        if (response?.profileStatus === "ACTIVE") {
          setActiveMember(true);
        }
        setShowSuccessPopup(true);
      } else if (isNormalUser) {
        const response = await handleNormalSignupMethod(
          registerDto,
          state.formData.profileImageFile,
          state.formData.videoFile,
        );
        if (response?.profileStatus === "ACTIVE") {
          setActiveMember(true);
        }
        setShowSuccessPopup(true);
      } else {
        const response = await handleSignUp(
          registerDto,
          state.formData.profileImageFile,
          state.formData.videoFile,
        );
        if (response?.profileStatus === "ACTIVE") {
          setActiveMember(true);
        }
        setShowSuccessPopup(true);
      }
    } catch (error: any) {
      setApiError(
        error.response?.data?.message || error.message || t("errors.generic"),
      );
      setState((prev) => ({
        ...prev,
        stage: "error",
        previousStage: prev.stage,
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = (): void => {
    if (state.stage === "personalDetails") {
      setState((prev) => ({
        ...prev,
        stage: "roleSelection",
        canGoBack: false,
      }));
    } else if (state.stage === "locationDetails") {
      setState((prev) => ({ ...prev, stage: "personalDetails" }));
    } else if (state.stage === "contactDetails") {
      setState((prev) => ({ ...prev, stage: "locationDetails" }));
    } else if (state.stage === "profileImageUpload") {
      setState((prev) => ({ ...prev, stage: "contactDetails" }));
    } else if (state.stage === "fighterFormBasic") {
      setState((prev) => ({ ...prev, stage: "profileImageUpload" }));
    } else if (state.stage === "fighterFormDetails") {
      setState((prev) => ({ ...prev, stage: "fighterFormBasic" }));
    } else if (state.stage === "fightHistory") {
      setState((prev) => ({ ...prev, stage: "fighterFormDetails" }));
    } else if (state.stage === "managerForm") {
      setState((prev) => ({ ...prev, stage: "profileImageUpload" }));
    } else if (state.stage === "videoUpload") {
      setState((prev) => ({ ...prev, stage: "fightHistory" }));
    } else if (state.stage === "sponsorStage") {
      setState((prev) => ({ ...prev, stage: "videoUpload" }));
    }

    setValidationErrors({});
  };

  const handleErrorClose = (): void => {
    setApiError(null);

    const user = JSON.parse(localStorage.getItem("user") || "null");

    if (!user) {
      localStorage.clear();
      window.location.href = "/login";
      return;
    }

    if (isEmailExistsError) {
      setIsEmailExistsError(false);
      localStorage.clear();
      window.location.href = "/login";
    } else {
      setState((prev) => ({
        ...prev,
        stage: prev.previousStage || "roleSelection",
        error: false,
      }));
    }
  };

  const handleSponsorLogoUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const maxSizeInBytes = 2 * 1024 * 1024;
    if (file.size > maxSizeInBytes) {
      toast.error("Image size must be less than 2MB");
      return;
    }
    setState((prev) => {
      const updatedFiles = [...(prev.formData.sponsorLogoFiles || [])];
      updatedFiles[index] = file;

      return {
        ...prev,
        formData: {
          ...prev.formData,
          sponsorLogoFiles: updatedFiles,
        },
      };
    });

    try {
      const response = await uploadMediaFile(file);
      updateSponsorLink(index, "logo", response);
      toast.success("Sponsor logo uploaded successfully");
    } catch (error: any) {
      toast.error(error?.response?.data?.message);
    }
  };

  const updateSponsorLink = (
    index: number,
    field: keyof fighterSponsorsRequestDtos,
    value: string,
  ) => {
    const updatedSponsors = [
      ...(state.formData.fighterDto?.fighterSponsorsRequestDtos || []),
    ];
    updatedSponsors[index] = {
      ...updatedSponsors[index],
      [field]: value,
    };

    handleFormChange(
      updatedSponsors,
      "fighterSponsorsRequestDtos",
      "fighterDto",
    );

    setValidationErrors((prev) => ({
      ...prev,
      [`fighterDto.fighterSponsorsRequestDtos.${index}.${field}`]: undefined,
    }));
  };

  return {
    state,
    isEmailExistsError,
    countries,
    regions,
    isSubmitting,
    selectedTrainingDisciplines,
    selectedWeightClasses,
    apiError,
    validationErrors,
    updateSponsorLink,
    handleSponsorLogoUpload,
    setValidationErrors,
    handleRoleSelection,
    handleNext,
    handleFormChange,
    toggleMultiSelectOption,
    handleFileChange,
    removeVideo,
    addYouTubeLink,
    updateYouTubeLink,
    removeYouTubeLink,
    addSocialMediaLink,
    updateSocialMediaLink,
    removeSocialMediaLink,
    handleFightHistoriesChange,
    handleFormSubmit,
    handleBack,
    handleErrorClose,
    showSuccessPopup,
    activeMember,
    setActiveMember,
    handleSuccessPopupClose,
  };
};

export default SignUpSetupLogics;
